/**
 * QUESTIONARIO DEPOIS DE CADA VIDEO DE TREINAMENTO (modulo Manual — decisao do Ruan, 29/09/2026).
 *
 * ⛔ **So REGISTRA, nao trava**: errar nao impede o proximo video. O resultado fica gravado pra
 * quem acompanha a vendedora nova (tabela `treinamento_respostas`, migration 0303).
 *
 * Desde 30/09/2026 (migration 0304) as perguntas moram no BANCO (`treinamento_perguntas`) e se
 * editam na tela "Gerenciar videos" — publicar deixou de exigir deploy e reinicio. Aqui ficam so'
 * as duas regras que a tela e o servidor precisam aplicar IGUAL: a correcao e a validacao de uma
 * pergunta.
 */
export type PerguntaTreinamento = {
  pergunta: string;
  alternativas: string[];
  /** Indice (0-based) da alternativa certa. */
  certa: number;
};

export const MIN_ALTERNATIVAS_TREINAMENTO = 2;
export const MAX_ALTERNATIVAS_TREINAMENTO = 6;

export type CorrecaoTreinamento = { acertos: number; total: number; certas: number[] };

/**
 * Corrige as respostas contra as perguntas ATIVAS do video, na ordem em que a tela as mostrou.
 * Devolve `null` se nao ha pergunta ou se a quantidade de respostas nao bate (toda pergunta e'
 * obrigatoria na tela — o botao so' libera com todas marcadas).
 */
export function corrigirQuestionarioTreinamento(
  perguntas: ReadonlyArray<Pick<PerguntaTreinamento, 'alternativas' | 'certa'>>,
  respostas: number[],
): CorrecaoTreinamento | null {
  if (perguntas.length === 0 || respostas.length !== perguntas.length) return null;
  let acertos = 0;
  for (let i = 0; i < perguntas.length; i++) {
    const r = respostas[i];
    const p = perguntas[i]!;
    if (!Number.isInteger(r) || r! < 0 || r! >= p.alternativas.length) return null;
    if (r === p.certa) acertos++;
  }
  return { acertos, total: perguntas.length, certas: perguntas.map((p) => p.certa) };
}

/**
 * O que falta pra uma pergunta poder ser gravada — lista vazia = pode. A tela usa isto pra dizer
 * por que o Salvar esta apagado; o servidor, pra recusar. `certa` null = ainda nao escolhida
 * (pergunta nova nasce SEM a certa marcada: marcar por padrao seria escolher por ele).
 */
export function problemasPerguntaTreinamento(p: { pergunta: string; alternativas: string[]; certa: number | null }): string[] {
  const problemas: string[] = [];
  if (!p.pergunta.trim()) problemas.push('escreva a pergunta');
  const alts = p.alternativas;
  if (alts.length < MIN_ALTERNATIVAS_TREINAMENTO) problemas.push(`ponha pelo menos ${MIN_ALTERNATIVAS_TREINAMENTO} alternativas`);
  if (alts.length > MAX_ALTERNATIVAS_TREINAMENTO) problemas.push(`no máximo ${MAX_ALTERNATIVAS_TREINAMENTO} alternativas`);
  if (alts.some((a) => !a.trim())) problemas.push('preencha todas as alternativas (ou apague a vazia)');
  if (p.certa === null) problemas.push('marque a resposta certa');
  else if (!Number.isInteger(p.certa) || p.certa < 0 || p.certa >= alts.length) problemas.push('a resposta certa não está na lista');
  return problemas;
}

/**
 * Numero do video na lista encadeada (pedido dele, 30/09/2026): secao 1, video 1 -> "1.01".
 * Posicoes 1-based, na ordem em que a lista mostra. Nao se grava — sai sempre da ordem atual.
 */
export function numeroVideoTreinamento(posSecao: number, posVideo: number): string {
  return `${posSecao}.${String(posVideo).padStart(2, '0')}`;
}

/** Slug a partir do titulo: minusculo, sem acento, palavras por hifen, ate 60 caracteres. */
export function slugTreinamento(titulo: string): string {
  return titulo
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
    .replace(/-+$/g, '');
}
